import React, { Component } from 'react';
import styled from 'styled-components';
import TreatmentsForm from 'components/treatments/TreatmentsForm';
import { ConsultationDto, CostEstimateRequest, PatientDto, TreatmentDto } from 'ts-types/api.types';
import { MessageSourceProps, withMessages } from "react-message-source";
import { deleteTreatment, undeleteTreatment } from 'services/treatmentServices';
import ToolbarButton from 'components/layout/ToolbarButton';
import {
  EditTreatmentValues,
  TreatmentMode,
  TreatmentsConsumerRenderProps
} from 'components/treatments/TreatmentsContext';
import { BillStatus } from 'ts-types/api.enums';
import TreatmentCostEstimate from 'components/treatments/TreatmentCostEstimate';
import ConfirmActionPopup from 'components/ConfirmActionPopup';
import axios, { CancelTokenSource } from 'axios';
import BillDetailsToolbarButton from 'components/bill/BillDetailsToolbarButton';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { errorUtils } from 'utils/errorUtils';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';
import TreatmentShowDeletedTreatmentsButton from 'components/treatments/TreatmentShowDeletedTreatmentsButton';

const TreatmentsToolbarContainer = styled.div`
  display: inline;
  flex: 1;
  text-align: right;

  .deleted-treatment {
    color: red;
  }
`;

interface Props extends MessageSourceProps, TreatmentsConsumerRenderProps, AuthConsumerRenderProps, NotificationSystemProps {
  currentPatient: PatientDto,
  currentConsultations: Array<ConsultationDto>,
  treatmentFormOpen: boolean,
  treatment?: EditTreatmentValues
}

interface State {
  treatmentCostEstimateModalOpen: boolean,
  errorMessage: string,
  cancelTokenSource: CancelTokenSource
}

class TreatmentsToolbar extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      treatmentCostEstimateModalOpen: false,
      errorMessage: "",
      cancelTokenSource
    };

  }

  toggleCostEstimateModal = () => {
    this.setState(({treatmentCostEstimateModalOpen}: State) => ({
      treatmentCostEstimateModalOpen: !treatmentCostEstimateModalOpen
    }));
  };

  removeTreatment = () => {
    const {selectedTreatment, currentPatient, deleteTreatment: removeTreatment, changeTreatments} = this.props;
    const {cancelTokenSource} = this.state;

    const isSelectedTreatmentDeleted = selectedTreatment!.deleted === 1;

    if (isSelectedTreatmentDeleted) {
      undeleteTreatment(selectedTreatment!.id, currentPatient.id, cancelTokenSource)
          .then(response =>  changeTreatments(response))
          .catch((e: any) => this.handleError(e.response.data));
    } else {
      deleteTreatment(selectedTreatment!.id, cancelTokenSource)
          .then(() => {
            removeTreatment(selectedTreatment!);
          }).catch((e: any) => this.handleError(e.response.data));
    }
  };

  handleError(error: any) {
    const {getMessage, selectionNotificationSystem} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [
      errorUtils.deleteTreatmentNotAllowed
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))

          if (violation.errorCode === errorUtils.deleteTreatmentNotAllowed) {
            //@ts-ignore
            const notification = selectionNotificationSystem.current;
            if (notification) {
              notification.addNotification({
                title: getMessage("error.header"),
                message: getMessage(`error.${violation.errorCode}`),
                level: 'error'
              });
            }
          }
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  }

  setErrorMessage = (errorMessage: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  shouldCostEstimationButtonBeDisabled = () => {
    const {selectedTreatment} = this.props;

    if (!selectedTreatment) {
      return true;
    } else if (!!selectedTreatment.costEstimationId) {
      return false;
    }

    return true;
  };

  seeTreatmentDetailedInformation = () => {
    this.props.getTreatment(TreatmentMode.DETAILS);
  };

  showDeletedTreatments = (treatments: Array<TreatmentDto>) => {
    const {changeTreatments} = this.props;

    changeTreatments(treatments);
  };

  copyTreatment = () => {
    this.props.getTreatment(TreatmentMode.COPY);
  };

  generateCostEstimateRequest = (): CostEstimateRequest | undefined => {

    const {selectedTreatment} = this.props;
    if (selectedTreatment) {
      return {
        tariffId: selectedTreatment.tariffId,
        treatmentId: selectedTreatment.id,
        costEstimationId: selectedTreatment.costEstimationId
      };
    }

    return undefined;
  };

  render() {
    const {treatmentCostEstimateModalOpen} = this.state;
    const {
      currentPatient,
      currentConsultations,
      getMessage,
      addTreatment,
      selectedTreatment,
      treatmentFormOpen,
      treatment,
      currentUser
    } = this.props;
    const currentPatientId = currentPatient ? currentPatient.id : -1;

    const isSelectedTreatmentDeleted = selectedTreatment
        ? selectedTreatment.deleted === 1
        : false;

    const shouldDisableButtons = !currentPatient
        || !selectedTreatment
        || (BillStatus.OPEN != selectedTreatment.billStatus)
        || (currentUser
            && currentUser.userConfig.mandateId !== selectedTreatment.mandateId);

    const disableDeleteButton = isSelectedTreatmentDeleted
        ? !isSelectedTreatmentDeleted
        : (shouldDisableButtons || !selectedTreatment);

    const costEstimateRequest = this.generateCostEstimateRequest();

    const editIconName = (shouldDisableButtons || isSelectedTreatmentDeleted) ? 'info circle' : 'edit outline';
    const editIconTooltipKey = (shouldDisableButtons || isSelectedTreatmentDeleted)
        ? 'treatment.detailedInfo'
        : 'treatment.edit';

    return (
        <TreatmentsToolbarContainer>
          <ToolbarButton iconName='plus'
                         iconSize='large'
                         disabled={!currentPatient}
                         onClick={this.props.toggleUpsertModal}
                         dataTooltip={getMessage('treatment.add')}
          />
          <ToolbarButton iconName='copy outline'
                         iconSize='large'
                         disabled={!selectedTreatment}
                         onClick={this.copyTreatment}
                         dataTooltip={getMessage('treatment.copy')}
          />
          <ToolbarButton iconName={editIconName}
                         iconSize='large'
                         disabled={!selectedTreatment}
                         onClick={() => {
                           if(shouldDisableButtons || isSelectedTreatmentDeleted){
                             this.seeTreatmentDetailedInformation()
                           } else {
                             this.props.getTreatment(TreatmentMode.EDIT)
                           }
                         }}
                         dataTooltip={getMessage(editIconTooltipKey)}
          />
          <TreatmentShowDeletedTreatmentsButton
              currentPatient={currentPatient}
              onClick={this.showDeletedTreatments}
          />
          <ConfirmActionPopup
              renderTarget={this.renderDeleteTrigger()}
              onConfirmAction={this.removeTreatment}
              disabled={disableDeleteButton}
              message={isSelectedTreatmentDeleted
                  ? getMessage('treatment.undelete.message')
                  : getMessage('treatment.delete.message')}
          />
          <ToolbarButton iconName='money bill alternate outline'
                         iconSize='large'
                         disabled={this.shouldCostEstimationButtonBeDisabled()}
                         onClick={this.toggleCostEstimateModal}
                         dataTooltip={getMessage('costEstimation')}
          />
          {treatmentFormOpen && (
              <TreatmentsForm
                  treatmentFormOpen={treatmentFormOpen}
                  toggleModal={this.props.toggleUpsertModal}
                  currentPatientId={currentPatientId}
                  addTreatment={addTreatment}
                  treatment={treatment}
                  editTreatment={() => this.props.getTreatment(TreatmentMode.EDIT)}
                  selectedTreatment={selectedTreatment}
              />)
          }
          <TreatmentCostEstimate
              modalOpen={treatmentCostEstimateModalOpen}
              onModalClose={this.toggleCostEstimateModal}
              costEstimateRequest={costEstimateRequest}
          />
          <BillDetailsToolbarButton
              currentTreatment={selectedTreatment}
              currentConsultations={currentConsultations}
          />
        </TreatmentsToolbarContainer>
    );
  }

  renderDeleteTrigger = (): React.ReactNode => {

    const {currentPatient, selectedTreatment, currentUser, getMessage} = this.props;

    const shouldDisableButtons = !currentPatient
        || !selectedTreatment
        || (BillStatus.OPEN != selectedTreatment.billStatus)
        || (currentUser
            && currentUser.userConfig.mandateId !== selectedTreatment.mandateId);

    const isSelectedTreatmentDeleted = selectedTreatment
        ? selectedTreatment.deleted === 1
        : false;


    const disableDeleteButton = isSelectedTreatmentDeleted
        ? !isSelectedTreatmentDeleted
        : (shouldDisableButtons || !selectedTreatment);

    return (
        <ToolbarButton iconName='trash alternate outline'
                       iconSize='large'
                       className={isSelectedTreatmentDeleted ? "deleted-treatment" : "treatment"}
                       disabled={disableDeleteButton}
                       dataTooltip={isSelectedTreatmentDeleted
                           ? getMessage('treatment.undelete')
                           : getMessage('treatment.delete')}
        />
    )
  }
}

export default withMessages(withAuthContext(withNotificationSystemContext(TreatmentsToolbar)));
